import config from "../config";
import { CaptureProfile, ELightPath } from "../Model/CaptureProfile";
import { CartesianCoordinate } from "../Model/Coordinate";
import { EMoveArmDirection } from "../Model/EMoveImagingArmButton";
import { EOpticFeature } from "../Model/EOpticFeature";
import { ImagerCapability } from "../Model/ImagerCapabilities";
import { ScheduleEntry } from "../Model/ImagingSchedule";
import { ESpeed } from "../Model/ModuleState";
import { sendDownloadImageRequest, sendLoadImageRequest } from "../Utility/FetchUtils";
import { sendGetRequest, sendPostRequest } from "./../Utility/FetchUtils";
import { EDropLocationCacheStatus } from "../Model/EDropLocationCacheStatus";

class ImagerApi {
  static BASE_URL = config.SERVICE_URL + "/imager";

  static setCurrentPlateAsReferencePlate(imagerIdentifier: string) {
    const url = this.BASE_URL + `/:set-as-reference-plate/${imagerIdentifier}`;
    return sendPostRequest(url);
  }

  static getContinuousZoomDof(imagerIdentifier: string, lightPath: ELightPath, zoom: number) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/${lightPath}/continuous-zoom-dof/${zoom}`;
    return sendGetRequest<number>(url);
  }

  static getCapabilities() {
    const url = ImagerApi.BASE_URL + "/capabilities";
    return sendGetRequest<ImagerCapability[]>(url);
  }

  static moveToWell(imagerIdentifier: string, wellNumber: number, dropNumber: number) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/well-drop/${wellNumber}/${dropNumber}`;
    return sendPostRequest(url);
  }

  static eject(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:eject`;
    return sendPostRequest(url);
  }

  static retract(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:retract`;
    return sendPostRequest(url);
  }

  static armStop(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:arm-stop`;

    return sendPostRequest(url);
  }

  static moveImagerArmWithSpeed(imagerIdentifier: string, direction: EMoveArmDirection, speed: ESpeed) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:arm-move/${direction}/${speed}`;
    return sendPostRequest(url);
  }

  static moveImagerArmToWellLimit(imagerIdentifier: string, direction: EMoveArmDirection, speed: number) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:arm-move-to-well-limit/${direction}/${speed}`;
    return sendPostRequest(url);
  }

  static moveImagerArmToTravelLimit(imagerIdentifier: string, direction: EMoveArmDirection, speed: number) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:arm-move-to-travel-limit/${direction}/${speed}`;
    return sendPostRequest(url);
  }

  static armMoveAbsolute(imagerIdentifier: string, coordinate: CartesianCoordinate) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/arm-absolute-position`;
    return sendPostRequest(url, coordinate);
  }

  static armMoveRelativeByFov(imagerIdentifier: string, percentageX: number, percentageY: number) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:arm-move-relative-by-fov/${percentageX}/${percentageY}`;
    return sendPostRequest(url);
  }

  static setLightpath(imagerIdentifier: string, lightPath: ELightPath) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/light-path/${lightPath}`;
    return sendPostRequest(url);
  }

  static setCaptureProfile(imagerIdentifier: string, captureProfile: CaptureProfile) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/capture-profile`;
    return sendPostRequest(url, captureProfile);
  }

  static getCaptureProfile(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/capture-profile`;
    return sendGetRequest<CaptureProfile>(url);
  }

  static getImageHistogram(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/image-histogram`;
    return sendGetRequest<number[]>(url);
  }

  static setLeveling(imagerIdentifier: string, lowerbound: number, upperbound: number) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/leveling/${lowerbound}/${upperbound}`;
    return sendPostRequest(url);
  }

  static autoLeveling(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:toggle-auto-leveling`;
    return sendPostRequest(url);
  }

  static startStreaming(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:start-streaming`;
    return sendPostRequest(url);
  }

  static stopStreaming(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:stop-streaming`;
    return sendPostRequest(url);
  }

  static autoExposure(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:auto-exposure`;
    return sendPostRequest(url);
  }

  static doAutoFocus(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:auto-focus`;
    return sendPostRequest(url);
  }

  static doLocateDrop(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:locate-drop`;
    return sendPostRequest(url);
  }

  static doEfi(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:efi`;
    return sendPostRequest(url);
  }

  static getEfiImagePaths(imagerIdentifier: string): Promise<string[]> {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/efi`;
    return sendGetRequest(url);
  }

  static efiImage(imagerIdentifier: string, path: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/efi/${path}`;
    return sendLoadImageRequest(url);
  }

  static downloadLastCapturedImage(imagerIdentifier: string, filename: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/last-captured-image`;
    return sendDownloadImageRequest(url, filename);
  }

  static lastCapturedImage(imagerIdentifier: string, lightPath: ELightPath) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/${lightPath}/last-captured-image`;
    return sendLoadImageRequest(url);
  }

  static homeOpticMotor(imagerIdentifier: string, lightPath: ELightPath, opticFeature: EOpticFeature) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/${lightPath}/optic-motor/${opticFeature}/:home`;
    return sendPostRequest(url);
  }

  static moveOpticMotor(
    imagerIdentifier: string,
    lightPath: ELightPath,
    opticFeature: EOpticFeature,
    position: number,
    speed: ESpeed
  ) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/${lightPath}/optic-motor/${opticFeature}/:move/${position}/${speed}`;
    return sendPostRequest(url);
  }

  static stopOpticMotor(imagerIdentifier: string, lightPath: ELightPath, opticFeature: EOpticFeature) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/${lightPath}/optic-motor/${opticFeature}/:stop`;
    return sendPostRequest(url);
  }

  static autoFocusImage(imagerIdentifier: string, path: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/auto-focus/${path}`;
    return sendLoadImageRequest(url);
  }

  static getLocateDropImagePaths(imagerIdentifier: string): Promise<string[]> {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/locate-drop`;
    return sendGetRequest(url);
  }

  static locateDropImage(imagerIdentifier: string, path: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/locate-drop/${path}`;
    return sendLoadImageRequest(url);
  }

  static doCancelImaging(imagerIdentifier: string) {
    const url = `${ImagerApi.BASE_URL}/${imagerIdentifier}/:cancel-imaging`;
    return sendPostRequest(url);
  }

  static doCancelSchedules(scheduleEntries: ScheduleEntry[]) {
    const url = `${ImagerApi.BASE_URL}/:cancel-schedules`;
    return sendPostRequest(url, scheduleEntries);
  }

  static doStartSchedules(scheduleEntries: ScheduleEntry[]) {
    const url = `${ImagerApi.BASE_URL}/:start-schedules`;
    return sendPostRequest(url, scheduleEntries);
  }

  static isDropLocationExist(barcode: string, wellNumber: number = 0, dropNumber: number = 0) {
    let url = "";
    if (wellNumber !== 0 && dropNumber !== 0) {
      url = `${ImagerApi.BASE_URL}/drop-location-exist/${encodeURIComponent(barcode)}/${wellNumber}/${dropNumber}`;
    } else {
      url = `${ImagerApi.BASE_URL}/drop-location-exist/${encodeURIComponent(barcode)}`;
    }
    return sendGetRequest<EDropLocationCacheStatus>(url);
  }

  static clearDropLocation(
    barcode: string,
    clearDropOverride: boolean,
    wellNumber: number = 0,
    dropNumber: number = 0
  ) {
    let url = "";
    if (wellNumber !== 0 && dropNumber !== 0) {
      url = `${ImagerApi.BASE_URL}/:clear-drop-location/${encodeURIComponent(barcode)}/${wellNumber}/${dropNumber}/${clearDropOverride}`;
    } else {
      url = `${ImagerApi.BASE_URL}/:clear-drop-location/${encodeURIComponent(barcode)}/${clearDropOverride}`;
    }
    return sendPostRequest(url);
  }

  static doStopSchedules(scheduleEntries: ScheduleEntry[]) {
    const url = `${ImagerApi.BASE_URL}/:stop-schedules`;
    return sendPostRequest(url, scheduleEntries);
  }

  static setBestFit(imagerIdentifier: string, enabled: boolean) {
    const url = `${ImagerApi.BASE_URL}/best-fit/${imagerIdentifier}/${enabled}`;
    return sendPostRequest(url);
  }
}

export default ImagerApi;
